import { computed, inject, Injectable, Signal, WritableSignal } from '@angular/core';
import { CalendarIntegrationStore, CommentStore, FocusStore, HabitStore, JournalStore, ListStore, MoneyTrackerStore, NoteStore, TodoStore, ViewSettingsStore } from '@app/_stores';
import { SettingsService } from '../settings.service';
import { DependencyService } from '../dependency.service';
import { MatDialog } from '@angular/material/dialog';
import { ModalService } from '../modal.service';
// NOTE: Form components are loaded dynamically to break circular dependencies
import { List } from '@app/_interfaces/list.interface';
import { Todo } from '@app/_interfaces/todo.interface';
import { Note } from '@app/_interfaces/note.interface';
import { MoneyTrackerSetup, MoneyTransaction } from '@app/_interfaces/money-tracker.interface';
import { JournalSetup } from '@app/_interfaces/journal.interface';
import { AlertService } from '../alert.service';
import { AttachmentType, CalendarIntegrationType, DayCode, EntityName, EntityStatus, HabitType } from '@app/_types/generic.type';
import { HabitSetup } from '@app/_interfaces/habit.interface';
import { CalendarIntegration } from '@app/_interfaces/calendar-integration.interface';
import { Subject, takeUntil } from 'rxjs';
import { CommonEntitySetup } from '@app/_interfaces/feature.interface';
import { CacheService } from '../cache.service';
import { HabitService } from './habit.service';
import { JournalService } from './journal.service';
import { Attachment, MembersModel, CustomDate } from '@app/_interfaces/generic.interface';
import { Collection } from '@app/_types/collection.type';
import { TodoService } from './todo.service';
import { CalendarIntegrationService } from './calendar-integration.service';
import { User } from '@app/_interfaces/user.interface';
import { FocusAttemptData } from '@app/_interfaces/focus-trainer.interface';
import { getLocalDateString as getDateString } from '@app/_services/date';
import { log } from '@app/_services/logger';
import { getRoleByUid } from '@app/_utils/members-model.utils';

@Injectable({
    providedIn: 'root',
})

export class FeatureService {

    readonly listStore = inject(ListStore);
    readonly noteStore = inject(NoteStore);
    readonly todoStore = inject(TodoStore);
    readonly habitStore = inject(HabitStore);
    readonly journalStore = inject(JournalStore);
    readonly moneyTrackerStore = inject(MoneyTrackerStore);
    readonly calendarStore = inject(CalendarIntegrationStore);
    readonly commentStore = inject(CommentStore);
    readonly focusStore = inject(FocusStore);
    readonly viewSettingsStore = inject(ViewSettingsStore);

    unSubscribe = new Subject<void>();

    earliestDateString: Signal<string> = computed(() => {
        // const todayDateString = getDateString();
        const todoDateString = this.todoStore.firstDateString();
        const habitDateString = this.habitStore.firstDateString();
        const journalDateString = this.journalStore.firstDateString();
        const moneyTrackerDateString = this.moneyTrackerStore.firstDateString();
        const calendarDateString = this.calendarStore.firstDateString();

        const dateStrings = [
            todoDateString,
            habitDateString,
            journalDateString,
            moneyTrackerDateString,
            calendarDateString
        ].filter(Boolean) as string[];

        // Convert YYYY-MM-DD → Date (app uses ISO date format)
        const toDate = (ds: string): Date => {
            const [year, month, day] = ds.split('-').map(Number);
            return new Date(year, month - 1, day);
        };

        // Find the minimum (oldest) date
        const minDate = dateStrings.reduce((min, current) => {
            return toDate(current) < toDate(min) ? current : min;
        }, dateStrings[0]);

        return minDate;
    });

    user: WritableSignal<User> = this.ss.user;
    subscriptionExpDate: Signal<Date | null> = computed(() => {
        return this.user()?.subscriptionInfo?.subscriptionExpDate || null;
    });

    constructor(
        private ss: SettingsService,
        private ds: DependencyService,
        private dialog: MatDialog,
        private modalService: ModalService,
        private alertService: AlertService,
        private cc: CacheService,
        private habitService: HabitService,
        private journalService: JournalService,
        private todoService: TodoService,
        private calendarIntegrationService: CalendarIntegrationService,

    ) {
    }

    /**
     * ✅ Check if a specific date has any visible entities
     * Uses the SAME logic as the UI display (getComputedEntities)
     * This ensures dots match what's actually shown
     */
    hasEntitiesOnDate(dateString: string): boolean {
        const settings = this.ss.viewSettings();

        // Get computed entities using the same services as the UI
        const computedTodos = settings?.featureSettings?.showTodoFeature
            ? this.todoStore.getComputedEntities(dateString)
            : { withTime: [], withoutTime: [] };
        const computedHabits = this.habitService.getComputedEntities(dateString, false);
        const computedJournals = this.journalService.getComputedEntities(dateString, false);
        const computedMoneyTracker = this.moneyTrackerStore.getComputedEntities(dateString);
        const computedCalendarEvents = this.calendarIntegrationService.getComputedEntities(dateString);
        const computedFocus = settings?.featureSettings?.showFocusFeature
            ? this.focusStore.getComputedEntities(dateString)
            : [];

        // Count all entities
        const totalCount =
            computedTodos.withTime.length +
            computedTodos.withoutTime.length +
            computedHabits.withTime.length +
            computedHabits.withoutTime.length +
            computedJournals.withTime.length +
            computedJournals.withoutTime.length +
            computedMoneyTracker.withTime.length +
            computedMoneyTracker.withoutTime.length +
            computedCalendarEvents.withTime.length +
            computedCalendarEvents.withoutTime.length +
            computedFocus.length;

        return totalCount > 0;
    }

    /**
     * ✅ Get hasData flags for multiple dates efficiently
     * Returns a map of dateString -> hasData
     */
    getHasDataForDates(dateStrings: string[]): Record<string, boolean> {
        const result: Record<string, boolean> = {};
        dateStrings.forEach(dateString => {
            result[dateString] = this.hasEntitiesOnDate(dateString);
        });
        return result;
    }

    async openTodoForm(todo: Todo | null, dateString: string | null, isAlert: boolean = false) {
        const { TodoFormComponent } = await import('@app/components/features/todos/todo-form/todo-form.component');
        const todoForm = this.modalService.openLevel1(TodoFormComponent,
            {
                mode: todo && todo.id ? 'edit' : 'new',
                value: todo ? todo : null,
                dateString: dateString
            },
            { disableClose: true }
        );

        return this.modalService.getClosedObservable(todoForm).subscribe(async result => {
            if (result && result.id && isAlert) {
                const isView = await this.alertService.toast('todoCreated_content', 'common_view', 'common_dismiss');
                if (!isView) return;
                const todo = this.todoStore.idToTodo()[result.id];
                this.openTodoForm(todo, result.dateString);
            }
        });
    }

    async openListForm(list?: List | null, isAlert: boolean = false) {
        const { ListFormComponent } = await import('@app/components/features/lists/list-form/list-form.component');
        const listForm = this.modalService.openLevel3(ListFormComponent, {
            mode: list && list.id ? 'edit' : 'new',
            value: list ? list : null
        }, {
            disableClose: true
        });
        return this.modalService.getClosedObservable(listForm).subscribe(async result => {
            if (result && result.id && isAlert) {
                const isView = await this.alertService.toast('listCreated_content', 'common_view', 'common_dismiss');
                if (!isView) return;
                const list = this.listStore.idToList()[result.id];
                const { ListItemsComponent } = await import('@app/components/features/lists/list-items/list-items.component');
                this.modalService.openLevel1(ListItemsComponent,
                    {
                        value: list
                    },
                    { autoFocus: false }
                );
            }
        });
    };

    async openNoteForm(note?: Note | null, isAlert: boolean = false) {
        const { NoteFormComponent } = await import('@app/components/features/notes/note-form/note-form.component');
        const noteForm = this.modalService.openLevel1(NoteFormComponent,
            {
                mode: note && note.id ? 'edit' : 'new',
                value: note ? note : null,
                isAlert: isAlert
            },
            { disableClose: true }
        );
        return this.modalService.getClosedObservable(noteForm).subscribe(async result => {
            if (result && result.id && isAlert) {
                const isView = await this.alertService.toast('noteCreated_content', 'common_view', 'common_dismiss');
                if (!isView) return;
                const note = this.noteStore.idToNote()[result.id];
                this.openNoteForm(note);
            }
        });
    };

    async openHabitTypeSelect() {
        const { HabitTypeSelectComponent } = await import('@app/components/features/habits/habit-type-select/habit-type-select.component');
        const habitTypeSelect = this.modalService.openModalLevel4(HabitTypeSelectComponent);
        return this.modalService.getClosedObservable(habitTypeSelect).subscribe(result => {
            if (result) {
                this.openHabitSetupForm(result, null, true);
            }
        });
    }

    async openHabitSetupForm(habitType: HabitType, setup?: HabitSetup | null, isAlert: boolean = false) {
        const { HabitSetupFormComponent } = await import('@app/components/features/habits/habit-setup-form/habit-setup-form.component');

        const data = {
            mode: setup && setup.id ? 'edit' : 'new',
            value: setup ? setup : null,
            habitType: habitType,
        };
        let setupHabitDialog;
        if (this.cc.isNarrowScreen()) {
            setupHabitDialog = this.modalService.openMainContent(
                HabitSetupFormComponent,
                data,
                { disableClose: true, autoFocus: false }
            );
        } else {
            setupHabitDialog = this.modalService.openLevel1(
                HabitSetupFormComponent,
                data,
                { disableClose: true, autoFocus: false }
            );
        }
        return this.modalService.getClosedObservable(setupHabitDialog).subscribe(async result => {
            if (result && result.id && isAlert) {
                const isView = await this.alertService.toast('habitSetupCreated_content', 'common_view', 'common_dismiss');
                if (!isView) return;
                const setup = this.habitStore.idToSetup()[result.id];
                this.openHabitSetupForm(setup.habitType, setup);
            }
        });
    }

    async openJournalSetupForm(setup?: JournalSetup | null, isAlert: boolean = false) {
        const { JournalSetupFormComponent } = await import('@app/components/features/journals/journal-setup-form/journal-setup-form.component');
        const data = {
            mode: setup && setup.id ? 'edit' : 'new',
            value: setup ? setup : null,
        }

        let setupDialog;
        if (this.cc.isNarrowScreen()) {
            setupDialog = this.modalService.openMainContent(
                JournalSetupFormComponent,
                data,
                { disableClose: true, autoFocus: false }
            );
        } else {
            setupDialog = this.modalService.openLevel1(
                JournalSetupFormComponent,
                data,
                { disableClose: true, autoFocus: false }
            );
        }

        return this.modalService.getClosedObservable(setupDialog).subscribe(async result => {
            if (result && result.id && isAlert) {
                const isView = await this.alertService.toast('journalSetupCreated_content', 'common_view', 'common_dismiss');
                if (!isView) return;
                const setup = this.journalStore.idToSetup()[result.id];
                this.openJournalSetupForm(setup);
            }
        });
    }

    async openMoneyTrackerSetupForm(setup?: MoneyTrackerSetup | null, isAlert: boolean = false) {
        const { MoneyTrackerSetupFormComponent } = await import('@app/components/features/money-tracker/money-tracker-setup-form/money-tracker-setup-form.component');
        const moneyData = {
            mode: setup && setup.id ? 'edit' : 'new',
            value: setup ? setup : null
        }
        let setupDialog;
        if (this.cc.isNarrowScreen()) {
            setupDialog = this.modalService.openMainContent(
                MoneyTrackerSetupFormComponent,
                moneyData,
                { disableClose: true, autoFocus: false }
            );
        } else {
            setupDialog = this.modalService.openLevel1(
                MoneyTrackerSetupFormComponent,
                moneyData,
                { disableClose: true, autoFocus: false }
            );
        }
        return this.modalService.getClosedObservable(setupDialog).subscribe(async result => {
            if (result && result.id && isAlert) {
                const isView = await this.alertService.toast('moneyTrackerCreated_setupContent', 'common_view', 'common_dismiss');
                if (!isView) return;
                const setup = this.moneyTrackerStore.idToSetup()[result.id];
                this.openMoneyTrackerSetupForm(setup);
            }
        });
    };

    async openMoneyTransactionForm(transactionType: 'income' | 'expense', transaction?: MoneyTransaction | null, isAlert: boolean = false) {
        const { MoneyTransactionFormComponent } = await import('@app/components/features/money-tracker/money-transaction-form/money-transaction-form.component');
        const transactionForm = this.modalService.openLevel1(
            MoneyTransactionFormComponent,
            {
                mode: transaction && transaction.id ? 'edit' : 'new',
                value: transaction ? transaction : null,
                transactionType: transactionType
            },
            { disableClose: true, autoFocus: false }
        );
        return this.modalService.getClosedObservable(transactionForm).subscribe(async result => {
            if (result && result.id && isAlert) {
                const isView = await this.alertService.toast(transactionType === 'income' ? 'moneyTrackerCreated_incomeContent' : 'moneyTrackerCreated_expenseContent', 'common_view', 'common_dismiss');
                if (!isView) return;
                const transaction = this.moneyTrackerStore.idToTransaction()[result.id];
                this.openMoneyTransactionForm(transaction.transactionType, transaction);
            }
        });
    };

    async openCalendarTypeSelect() {
        const { CalendarTypeSelectComponent } = await import('@app/components/features/calendar-integration/calendar-type-select/calendar-type-select.component');
        const calendarTypeSelect = this.modalService.openModalLevel4(CalendarTypeSelectComponent);
        return this.modalService.getClosedObservable(calendarTypeSelect).subscribe(result => {
            if (result) {
                this.openCalendarConnectForm(result, true);
            }
        });
    };

    async openCalendarConnectForm(type: CalendarIntegrationType, isAlert: boolean = false) {
        const { CalendarConnectComponent } = await import('@app/components/features/calendar-integration/calendar-connect/calendar-connect.component');
        const calendarConnectDialog = this.dialog.open(CalendarConnectComponent, {
            width: '100%',
            maxWidth: '500px',
            height: 'auto',
            disableClose: true,
            data: {
                type: type,
                isAlert: isAlert
            },
        });
        return calendarConnectDialog.afterClosed().subscribe(async result => {
            if (result && isAlert) {
                const isView = await this.alertService.toast('connectCalendarSuccessfull_content', 'common_view', 'common_dismiss');
                if (!isView) return;
                const calendarAccountData = this.calendarStore.idToAccount()[result.id];
                this.openCalendarAccountForm('edit', calendarAccountData);
            }
        });
    };

    async openCalendarAccountForm(mode: 'new' | 'edit', calendarAccountData: CalendarIntegration, isAlert: boolean = false) {
        const { CalendarAccountFormComponent } = await import('@app/components/features/calendar-integration/calendar-account-form/calendar-account-form.component');
        if (this.cc.isNarrowScreen()) {
            const setupCalendarDialog = this.modalService.openMainContent(CalendarAccountFormComponent, { mode: mode, value: calendarAccountData }, { disableClose: true })
        } else {
            const setupCalendarDialog = this.modalService.openModalLevel1(CalendarAccountFormComponent, { mode: mode, value: calendarAccountData }, { disableClose: true })
        }

    };

    async openComments() {
        const { CommentsListComponent } = await import('@app/components/addons/comments/comments-list/comments-list.component');
        const commentsDialog = this.modalService.openLevel2(
            CommentsListComponent,
            {
            },
            { disableClose: true }
        );
    };

    // Complete solution for checking active dates

    getActiveEnitysByDate(
        fromDateString: string,
        toDateString: string
    ): { computedData: Record<string, CommonEntitySetup[]>, entityIds: string[] } {
        const settings = this.ss.viewSettings();
        const hideCompleted = this.viewSettingsStore.hideCompletedItems();

        // ✅ Get visibility settings
        const showTodoFeature = this.viewSettingsStore.showTodoFeature();
        const hiddenHabitsMap = this.viewSettingsStore.hiddenHabits() || {};
        const hiddenJournalsMap = this.viewSettingsStore.hiddenJournals() || {};
        const hiddenMoneytrackersMap = this.viewSettingsStore.hiddenMoneytrackers() || {};
        const hiddenCalendarsMap = this.viewSettingsStore.hiddenCalendars() || {};

        // ✅ Filter entities based on visibility settings
        const allTodos = showTodoFeature ? this.todoStore.todos().filter(t => !t.deletedAt) : [];
        const allHabitSetups = this.habitStore.setups().filter(
            (setup: any) => !setup.deletedAt && !hiddenHabitsMap[setup.id]
        );
        const allJournalSetups = this.journalStore.setups().filter(
            (setup: any) => !setup.deletedAt && !hiddenJournalsMap[setup.id]
        );
        const allMoneyTrackers = this.moneyTrackerStore.transactions().filter(
            (transaction: any) => !transaction.deletedAt && !hiddenMoneytrackersMap[transaction.setupId]
        );
        // Get active (non-deleted) calendar integration IDs to filter orphaned event setups
        const activeCalendarIntegrationIds = new Set(
            this.calendarStore.accounts().filter(a => !a.deletedAt).map(a => a.id)
        );
        // Build a set of disabled calendar group IDs (calendarId values from event setups)
        // A calendarGroup is disabled when syncStatus === 'disabled'
        const disabledCalendarGroupIds = new Set<string>();
        this.calendarStore.accounts().forEach(account => {
            if (account.deletedAt) return;
            account.calendarGroups?.forEach(group => {
                if (group.syncStatus === 'disabled') {
                    disabledCalendarGroupIds.add(group.id);
                }
            });
        });
        const allCalendarEvents = this.calendarStore.eventSetups().filter(
            (event: any) => !event.deletedAt &&
                !hiddenCalendarsMap[event.calendarIntegrationId] &&
                activeCalendarIntegrationIds.has(event.calendarIntegrationId) &&
                !disabledCalendarGroupIds.has(event.calendarId)
        );

        const allEntities: {
            id?: string,
            title: string,
            entityType?: string,
            startAt?: CustomDate | null,
            endAt?: CustomDate | null,
            dueAt?: CustomDate | null,
            repeat?: string[],
            members?: MembersModel,
            tags?: string[],
            setupId?: string,
            calendarGroupId?: string
        }[] = [...allTodos, ...allHabitSetups, ...allJournalSetups, ...allMoneyTrackers, ...allCalendarEvents];

        const availableEntities: Record<string, CommonEntitySetup[]> = {};
        const entityIds: string[] = [];

        // ✅ Initialize the record with empty arrays for each date in range
        const currentDate = new Date(fromDateString);
        const endDate = new Date(toDateString);

        while (currentDate <= endDate) {
            const dateString = [
                currentDate.getFullYear(),
                String(currentDate.getMonth() + 1).padStart(2, '0'),
                String(currentDate.getDate()).padStart(2, '0'),
            ].join('-');

            availableEntities[dateString] = [];
            currentDate.setDate(currentDate.getDate() + 1);
        }

        // ✅ Process each entity and check for active dates
        allEntities.forEach(entity => {
            const availableDateStrings = this.checkActiveDateInRange(
                fromDateString,
                toDateString,
                (entity.entityType || 'todos') as Collection,
                entity.startAt || null,
                entity.endAt || null,
                entity.repeat || [],
                entity.dueAt || null
            );

            // ✅ Add entity to each active date
            availableDateStrings.forEach(dateString => {
                if (availableEntities[dateString]) {
                    availableEntities[dateString].push(entity as CommonEntitySetup);
                }
            });

            // ✅ Track entity IDs
            if (availableDateStrings.length > 0) {
                entityIds.push(entity.title || '');
            }
        });

        return { computedData: availableEntities, entityIds: entityIds };
    }

    /**
     * ✅ IMPROVED: Check if entity is active on dates within the given range
     * Returns array of date strings where the entity is active
     */
    checkActiveDateInRange(
        fromDateString: string,
        toDateString: string,
        collection: Collection,
        startAt: CustomDate | null,
        endAt: CustomDate | null,
        repeat: string[],
        dueAt: CustomDate | null
    ): string[] {
        const activeDates: string[] = [];

        if (collection === 'moneyTrackerActions') {
            // ✅ For money transactions, check only dueAt date
            if (!dueAt) return [];

            const startsBeforeOrOn = dueAt.dateString >= fromDateString;
            const endsAfterOrOn = dueAt.dateString <= toDateString;
            const isInDateRange = startsBeforeOrOn && endsAfterOrOn;

            if (!isInDateRange) return [];
            return [dueAt.dateString];
        } else {
            // ✅ For other entities (todos, habits, journals, events)
            if (!startAt) return [];

            // ✅ Check if startAt and endAt bounds contain the date range
            const startsBeforeOrOn = startAt.dateString <= toDateString;
            const endsAfterOrOn = endAt ? endAt.dateString >= fromDateString : true;
            const isInDateRange = startsBeforeOrOn && endsAfterOrOn;

            if (!isInDateRange) return [];

            if (repeat.length === 0) {
                // ✅ Non-repeating entity: check if startAt is within range
                if (startAt.dateString >= fromDateString && startAt.dateString <= toDateString) {
                    activeDates.push(startAt.dateString);
                }
            } else {
                // ✅ Repeating entity: get all dates within the range
                const ruleRecord = this.ruleToRuleRecord(repeat[0] || '');
                const eligibleDates = this.getAllDatesOfEntity(
                    ruleRecord,
                    startAt.dateString,
                    endAt?.dateString || '9999-12-31',
                    fromDateString,
                    toDateString
                );
                activeDates.push(...eligibleDates);
            }

            return activeDates;
        }
    }

    /**
     * ✅ NEW: Check if entity is active on a specific date
     * Useful for individual day checks in breadcrumb
     */
    isEntityActiveOnDate(
        dateString: string,
        collection: Collection,
        startAt: CustomDate | null,
        endAt: CustomDate | null,
        repeat: string[],
        dueAt: CustomDate | null,
        deletedAt: CustomDate | null
    ): boolean {
        if (collection === 'moneyTrackerActions') {
            // ✅ For money transactions, check only dueAt date
            if (!dueAt) return false;
            return dueAt.dateString === dateString;
        } else {
            // ✅ For other entities
            if (!startAt) return false;
            if (deletedAt) return false;

            // ✅ Check if date is within start and end bounds
            const startsBeforeOrOn = startAt.dateString <= dateString;
            const endsAfterOrOn = endAt ? endAt.dateString >= dateString : true;
            const isInDateRange = startsBeforeOrOn && endsAfterOrOn;

            if (!isInDateRange) return false;

            if (repeat.length === 0) {
                // ✅ Non-repeating: check if it's the start date
                return startAt.dateString === dateString;
            } else {
                // ✅ Repeating: check if date matches the recurrence rule
                const ruleRecord = this.ruleToRuleRecord(repeat[0] || '');
                const eligibleDates = this.getAllDatesOfEntity(
                    ruleRecord,
                    startAt.dateString,
                    endAt?.dateString || '9999-12-31',
                    dateString,
                    dateString
                );
                return eligibleDates.includes(dateString);
            }
        }
    }

    /**
     * ✅ NEW: Get hasData flag for a single date
     * Returns true if any entity is active on that date
     */
    hasDataOnDate(dateString: string, allEntities: any[]): boolean {
        return allEntities.some(entity => {
            return this.isEntityActiveOnDate(
                dateString,
                (entity.entityType || 'todos') as Collection,
                entity.startAt || null,
                entity.endAt || null,
                entity.repeat || [],
                entity.dueAt || null,
                entity.deletedAt || null
            );
        });
    }

    /**
     * ✅ NEW: Mark hasData flag for week days
     * Use this to update the breadcrumb week days with data indicators
     */
    markWeekDataFlagsOptimized(weekDays: any[], allEntities: any[]): any[] {
        const weekDaysCopy = [...weekDays]; // ✅ Create a copy to avoid mutating the original array
        weekDaysCopy.forEach(day => {
            const dateString = [
                day.dateObject.getFullYear(),
                String(day.dateObject.getMonth() + 1).padStart(2, '0'),
                String(day.dateObject.getDate()).padStart(2, '0'),
            ].join('-');

            // ✅ Check if any entity is active on this date
            day.hasData = allEntities.some(entity => {
                return this.isEntityActiveOnDate(
                    dateString,
                    (entity.entityType || 'todos') as Collection,
                    entity.startAt || null,
                    entity.endAt || null,
                    entity.repeat || [],
                    entity.dueAt || null,
                    entity.deletedAt || null
                );
            });
        });

        return weekDaysCopy; // ✅ Return the updated copy
    }

    // Filter function revamp

    getEnitysByDate(fromDateString: string, toDateString: string, filterInvalidEntries: boolean = true, filterCompleted: boolean = false, filterQuery: string = '', filterFeatures: string[] = [], filterAccess: string[] = [], filterTags: string[] = [], filterStatus: EntityStatus[] = [], filterAttachments: AttachmentType[] = [], filterMoods: number[] = [], filterStartDate: Date | null = null, filterEndDate: Date | null = null): { computedData: Record<string, CommonEntitySetup[]>, entityIds: string[] } {
        const settings = this.ss.viewSettings();
        const hideCompleted = this.viewSettingsStore.hideCompletedItems();

        // ✅ Get visibility settings
        const showTodoFeature = this.viewSettingsStore.showTodoFeature();
        const showFocusFeature = this.viewSettingsStore.showFocusFeature();
        const hiddenHabitsMap = this.viewSettingsStore.hiddenHabits() || {};
        const hiddenJournalsMap = this.viewSettingsStore.hiddenJournals() || {};
        const hiddenMoneytrackersMap = this.viewSettingsStore.hiddenMoneytrackers() || {};
        const hiddenCalendarsMap = this.viewSettingsStore.hiddenCalendars() || {};

        // ✅ Early exit if feature filter excludes everything
        const shouldShowFocus = filterFeatures.length === 0 || filterFeatures.includes('focus');
        const shouldProcessRegularEntities = filterFeatures.length === 0 ||
            filterFeatures.some(f => ['todo', 'habit', 'journal', 'moneyTracker', 'calendarIntegration'].includes(f));

        // ✅ CRITICAL FIX: Focus should NOT show when non-applicable filters are applied
        // Focus only supports: feature filter, access filter, date range filter
        // Focus does NOT support: query, tags, attachments, mood, status filters
        const hasNonFocusFilters = filterQuery.length > 0 ||
            filterTags.length > 0 ||
            filterAttachments.length > 0 ||
            filterMoods.length > 0 ||
            filterStatus.length > 0;

        const shouldActuallyShowFocus = shouldShowFocus && !hasNonFocusFilters;

        if (!shouldProcessRegularEntities && !shouldActuallyShowFocus) {
            return { computedData: {}, entityIds: [] };
        }

        const availableEntities: Record<string, CommonEntitySetup[]> = {};
        const entityIds: string[] = [];

        // ========================================
        // PROCESS REGULAR ENTITIES (Todo, Habit, Journal, Money Tracker, Calendar)
        // ========================================
        if (shouldProcessRegularEntities) {
            // ✅ Filter entities based on visibility settings
            const allTodos = showTodoFeature ? this.todoStore.todos().filter(t => !t.deletedAt) : [];
            const allHabitSetups = this.habitStore.setups().filter(
                (setup: any) => !setup.deletedAt && !hiddenHabitsMap[setup.id]
            );
            const allJournalSetups = this.journalStore.setups().filter(
                (setup: any) => !setup.deletedAt && !hiddenJournalsMap[setup.id]
            );
            const allMoneyTrackers = this.moneyTrackerStore.transactions().filter(
                (transaction: any) => !transaction.deletedAt && !hiddenMoneytrackersMap[transaction.setupId]
            );
            // Get active (non-deleted) calendar integration IDs to filter orphaned event setups
            const activeCalendarIntegrationIds = new Set(
                this.calendarStore.accounts().filter(a => !a.deletedAt).map(a => a.id)
            );
            // Build a set of disabled calendar group IDs (calendarId values from event setups)
            // A calendarGroup is disabled when syncStatus === 'disabled'
            const disabledCalendarGroupIds = new Set<string>();
            this.calendarStore.accounts().forEach(account => {
                if (account.deletedAt) return;
                account.calendarGroups?.forEach(group => {
                    if (group.syncStatus === 'disabled') {
                        disabledCalendarGroupIds.add(group.id);
                    }
                });
            });
            const allCalendarEvents = this.calendarStore.eventSetups().filter(
                (event: any) => !event.deletedAt &&
                    !hiddenCalendarsMap[event.calendarIntegrationId] &&
                    activeCalendarIntegrationIds.has(event.calendarIntegrationId) &&
                    !disabledCalendarGroupIds.has(event.calendarId)
            );

            const allEntities: any[] = [...allTodos, ...allHabitSetups, ...allJournalSetups, ...allMoneyTrackers, ...allCalendarEvents];

            // Pre-compute filter date objects for performance (avoid creating multiple times in loop)
            const filterStartDateObj = filterStartDate ? new Date(filterStartDate) : null;
            const filterEndDateObj = filterEndDate ? new Date(filterEndDate) : null;
            if (filterStartDateObj) filterStartDateObj.setHours(0, 0, 0, 0);
            if (filterEndDateObj) filterEndDateObj.setHours(23, 59, 59, 999);

            // Process each entity
            allEntities.forEach(entity => {
                if (entity.entityType !== 'moneyTrackerActions') {
                    if (entity.deletedAt) return;
                } else {
                    entity.dueAt = entity.transactionDate || null;
                }

                const availableDateStrings = this.checkDateInRange(
                    fromDateString,
                    toDateString,
                    (entity.entityType || 'todos') as Collection,
                    entity.startAt || null,
                    entity.endAt || null,
                    entity.repeat || [],
                    entity.dueAt || null,
                    entity.deletedAt || null
                );

                availableDateStrings.forEach(dateString => {
                    availableEntities[dateString] ??= [];

                    const entitySetup = this.getEntitySetup(entity, dateString);

                    // Apply all filters
                    if (!this.passesAllFilters(
                        entitySetup,
                        dateString,
                        filterQuery,
                        filterFeatures,
                        filterAccess,
                        filterTags,
                        filterStatus,
                        filterAttachments,
                        filterMoods,
                        filterStartDateObj,
                        filterEndDateObj
                    )) {
                        return;
                    }

                    availableEntities[dateString].push(entitySetup);
                    entityIds.push(entitySetup.id);
                });
            });
        }

        // ========================================
        // PROCESS FOCUS ENTITIES
        // ========================================
        if (showFocusFeature && shouldActuallyShowFocus) {
            // Pre-compute filter date objects for performance
            const filterStartDateObj = filterStartDate ? new Date(filterStartDate) : null;
            const filterEndDateObj = filterEndDate ? new Date(filterEndDate) : null;
            if (filterStartDateObj) filterStartDateObj.setHours(0, 0, 0, 0);
            if (filterEndDateObj) filterEndDateObj.setHours(23, 59, 59, 999);

            const focusPushedKeys = new Set<string>();
            const allFocuses = this.focusStore.focus().filter(f => !f.deletedAt);

            allFocuses.forEach(entity => {
                const entityAttempts = Object.values(entity.attempts);

                entityAttempts.forEach((attempt: FocusAttemptData) => {
                    const dateString = getDateString(attempt.startAt);

                    // Early exit: date range check
                    if (dateString < fromDateString || dateString > toDateString) return;

                    // Early exit: duplicate check
                    const key = `${entity.id}-${dateString}`;
                    if (focusPushedKeys.has(key)) return;
                    focusPushedKeys.add(key);

                    // Initialize array if needed
                    availableEntities[dateString] ??= [];

                    const entitySetup = this.getEntitySetup(entity, dateString);

                    // Apply Focus-specific filters (only access and date range)
                    if (!this.passesFocusFilters(
                        entitySetup,
                        dateString,
                        filterAccess,
                        filterStartDateObj,
                        filterEndDateObj
                    )) {
                        return;
                    }

                    availableEntities[dateString].push(entitySetup);
                    entityIds.push(entitySetup.id);
                });
            });
        }

        return { computedData: availableEntities, entityIds };
    }

    /**
     * ✅ Check if entity passes all filters (for regular entities)
     * Centralized filter logic for better maintainability
     */
    private passesAllFilters(
        entitySetup: CommonEntitySetup,
        dateString: string,
        filterQuery: string,
        filterFeatures: string[],
        filterAccess: string[],
        filterTags: string[],
        filterStatus: EntityStatus[],
        filterAttachments: AttachmentType[],
        filterMoods: number[],
        filterStartDateObj: Date | null,
        filterEndDateObj: Date | null,
        showInvalidEntries: boolean = false
    ): boolean {
        // Invalid entry filter
        // When showInvalidEntries is false, filter out invalid entries
        // Use invalidEntry flag (not status) to distinguish:
        // - Valid entries (should appear per repeat rules) have invalidEntry = false
        // - Invalid entries (instances that shouldn't exist per rules) have invalidEntry = true
        if (!showInvalidEntries && entitySetup.invalidEntry === true) {
            return false;
        }

        // Query filter
        if (filterQuery && !entitySetup.title.toLowerCase().includes(filterQuery.toLowerCase())) {
            return false;
        }

        // Feature filter
        if (filterFeatures.length > 0 && !filterFeatures.includes(entitySetup.entityName)) {
            return false;
        }

        // ========================================
        // OR LOGIC FOR CONTENT FILTERS (access, tags, attachments, status)
        // Show entity if it matches ANY of these filter categories
        // Example: If user selects both "hashtag" and "status",
        // show entities that have EITHER hashtags OR matching status (not necessarily both)
        // ========================================
        const hasAccessFilter = filterAccess.length > 0;
        const hasTagsFilter = filterTags.length > 0;
        const hasAttachmentsFilter = filterAttachments.length > 0;
        const hasStatusFilter = filterStatus.length > 0;
        // const hasMoodsFilter = filterMoods.length > 0; // Mood filter not currently used

        // If any content filters are active, check OR logic
        if (hasAccessFilter || hasTagsFilter || hasAttachmentsFilter || hasStatusFilter) {
            let passesContentFilter = false;

            // Check access/role filter - show if entity's role matches ANY selected role
            if (hasAccessFilter && filterAccess.includes(entitySetup.role)) {
                passesContentFilter = true;
            }

            // Check tags filter - show if ANY selected tag exists on the entity
            if (hasTagsFilter && filterTags.some(tag => entitySetup.tags.includes(tag))) {
                passesContentFilter = true;
            }

            // Check attachments filter - show if ANY selected attachment type exists on the entity
            if (hasAttachmentsFilter && filterAttachments.some(attachmentType => entitySetup.attachmentTypes.includes(attachmentType))) {
                passesContentFilter = true;
            }

            // Check status filter - show if entity's status matches ANY selected status
            if (hasStatusFilter) {
                const status = entitySetup.status;
                const percentage = entitySetup.percentage ?? 0;

                for (const fStatus of filterStatus) {
                    if (fStatus === status) {
                        passesContentFilter = true;
                        break;
                    }

                    // "incomplete" should include "missed"
                    if (fStatus === 'incomplete' && status !== 'completed' && status !== 'skipped') {
                        passesContentFilter = true;
                        break;
                    }

                    // "inprogress" should include "draft" or any status with percentage < 100
                    if (fStatus === 'inprogress' && (status === 'draft' || (percentage > 0 && percentage < 100))) {
                        passesContentFilter = true;
                        break;
                    }
                }
            }

            // Mood filter - Currently not used, commented out
            // if (hasMoodsFilter && entitySetup.mood !== null && filterMoods.includes(entitySetup.mood)) {
            //     passesContentFilter = true;
            // }

            // If content filters are active but entity doesn't match ANY of them, exclude it
            if (!passesContentFilter) {
                return false;
            }
        }

        // Date range filter
        if (filterStartDateObj || filterEndDateObj) {
            const entityDate = new Date(dateString);
            entityDate.setHours(0, 0, 0, 0);

            if (filterStartDateObj && entityDate < filterStartDateObj) {
                return false;
            }

            if (filterEndDateObj) {
                const entityDateEnd = new Date(dateString);
                entityDateEnd.setHours(23, 59, 59, 999);

                if (entityDateEnd > filterEndDateObj) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * ✅ Check if focus entity passes filters (only access and date range)
     * Focus doesn't support tags, attachments, moods, status, or query filters
     */
    private passesFocusFilters(
        entitySetup: CommonEntitySetup,
        dateString: string,
        filterAccess: string[],
        filterStartDateObj: Date | null,
        filterEndDateObj: Date | null
    ): boolean {
        // Access/Role filter
        if (filterAccess.length > 0 && !filterAccess.includes(entitySetup.role)) {
            return false;
        }

        // Date range filter
        if (filterStartDateObj || filterEndDateObj) {
            const entityDate = new Date(dateString);
            entityDate.setHours(0, 0, 0, 0);

            if (filterStartDateObj && entityDate < filterStartDateObj) {
                return false;
            }

            if (filterEndDateObj) {
                const entityDateEnd = new Date(dateString);
                entityDateEnd.setHours(23, 59, 59, 999);

                if (entityDateEnd > filterEndDateObj) {
                    return false;
                }
            }
        }

        return true;
    };

    // ============================================================================
    // SINGLE DATE LOOKUP (Optimized for Pagination)
    // ============================================================================

    /**
     * ✅ Check if an entity appears on a specific date
     * This is O(1) for non-repeating entities and O(1) RRULE check for repeating entities
     */
    entityAppearsOnDate(
        entity: any,
        dateString: string,
        showInvalidEntries = false
    ): boolean {
        const collection = entity.entityType as Collection;

        // Money transactions: check dueAt/transactionDate
        if (collection === 'moneyTrackerActions') {
            const dueAt = entity.transactionDate || entity.dueAt;
            if (!dueAt) return false;
            return dueAt.dateString === dateString;
        }

        // Other entities: check startAt and repeat rules
        const startAt = entity.startAt;
        if (!startAt) return false;

        // Non-repeating: simple date match
        if (!entity.repeat || entity.repeat.length === 0) {
            return startAt.dateString === dateString;
        }

        // Repeating: check bounds first (fast rejection)
        if (dateString < startAt.dateString) return false;
        if (entity.endAt && dateString > entity.endAt.dateString) return false;

        // Check RRULE match (O(1) check for single date)
        const ruleRecord = this.ruleToRuleRecord(entity.repeat[0] || '');

        const eligible = this.isElligible(
            new Date(dateString + 'T00:00:00'),
            ruleRecord
        );

        // If eligible per repeat rules, check if an exception replaces this date
        if (eligible) {
            // For calendar events: check if a moved/cancelled exception replaces this RRULE date
            if (collection === 'calendarEventSetups') {
                const setupToEventsMap = this.calendarStore.setupToEventsMap();
                const eventsForSetup = setupToEventsMap[entity.id];
                if (eventsForSetup) {
                    for (const event of Object.values(eventsForSetup)) {
                        if (event.originalStartAt?.dateString === dateString) {
                            return false;
                        }
                    }
                }
            }
            return true;
        }

        // Calendar events: always check for exception occurrences (not gated by showInvalidEntries)
        if (collection === 'calendarEventSetups') {
            const setupToEventsMap = this.calendarStore.setupToEventsMap();
            const eventOnDate = setupToEventsMap[entity.id]?.[dateString];
            if (eventOnDate && eventOnDate.originalStartAt && !eventOnDate.deletedAt) {
                return true;
            }
        }

        // If not eligible but showInvalidEntries is true, check if instance exists (invalid entry)
        if (showInvalidEntries) {
            // Check for habit instance
            if (collection === 'habitSetups') {
                const habitInstance = this.habitStore.habitMap().setups[entity.id]?.[dateString];
                if (habitInstance) return true;
            }
            // Check for journal instance
            else if (collection === 'journalSetups') {
                const journalInstance = this.journalStore.journalMap().setups[entity.id]?.[dateString];
                if (journalInstance) return true;
            }
        }

        return false;
    }

    /**
     * ✅ Get all entities for a SINGLE date with filters applied
     * Optimized for pagination - processes one date at a time
     */
    getEntitiesForSingleDate(
        dateString: string,
        filterQuery: string = '',
        filterFeatures: EntityName[] = [],
        filterAccess: string[] = [],
        filterTags: string[] = [],
        filterStatus: EntityStatus[] = [],
        filterAttachments: AttachmentType[] = [],
        filterMoods: number[] = [],
        filterStartDate: Date | null = null,
        filterEndDate: Date | null = null,
        showInvalidEntries: boolean = false
    ): CommonEntitySetup[] {
        const entities: CommonEntitySetup[] = [];

        // Get visibility settings
        const showTodoFeature = this.viewSettingsStore.showTodoFeature();
        const showFocusFeature = this.viewSettingsStore.showFocusFeature();
        const hiddenHabitsMap = this.viewSettingsStore.hiddenHabits() || {};
        const hiddenJournalsMap = this.viewSettingsStore.hiddenJournals() || {};
        const hiddenMoneytrackersMap = this.viewSettingsStore.hiddenMoneytrackers() || {};
        const hiddenCalendarsMap = this.viewSettingsStore.hiddenCalendars() || {};
        // Get active (non-deleted) calendar integration IDs to filter orphaned event setups
        const activeCalendarIntegrationIds = new Set(
            this.calendarStore.accounts().filter(a => !a.deletedAt).map(a => a.id)
        );
        // Build a set of disabled calendar group IDs (calendarId values from event setups)
        // A calendarGroup is disabled when syncStatus === 'disabled'
        const disabledCalendarGroupIds = new Set<string>();
        this.calendarStore.accounts().forEach(account => {
            if (account.deletedAt) return;
            account.calendarGroups?.forEach(group => {
                if (group.syncStatus === 'disabled') {
                    disabledCalendarGroupIds.add(group.id);
                }
            });
        });

        // Check if we should process regular entities vs focus
        const shouldShowFocus = filterFeatures.length === 0 || filterFeatures.includes('focus');
        const shouldProcessRegularEntities = filterFeatures.length === 0 ||
            filterFeatures.some(f => ['todo', 'habit', 'journal', 'moneyTracker', 'calendarIntegration'].includes(f));

        // Focus doesn't support certain filters
        const hasNonFocusFilters = filterQuery.length > 0 ||
            filterTags.length > 0 ||
            filterAttachments.length > 0 ||
            filterMoods.length > 0 ||
            filterStatus.length > 0;

        const shouldActuallyShowFocus = shouldShowFocus && !hasNonFocusFilters;

        // Pre-compute filter date objects
        const filterStartDateObj = filterStartDate ? new Date(filterStartDate) : null;
        const filterEndDateObj = filterEndDate ? new Date(filterEndDate) : null;
        if (filterStartDateObj) filterStartDateObj.setHours(0, 0, 0, 0);
        if (filterEndDateObj) filterEndDateObj.setHours(23, 59, 59, 999);

        // Process regular entities
        if (shouldProcessRegularEntities) {
            // Todos
            if (showTodoFeature && (filterFeatures.length === 0 || filterFeatures.includes('todo'))) {
                this.todoStore.todos()
                    .filter(t => !t.deletedAt)
                    .filter(t => this.entityAppearsOnDate(t, dateString, showInvalidEntries))
                    .forEach(entity => {
                        const entitySetup = this.getEntitySetup(entity, dateString);
                        if (this.passesAllFilters(entitySetup, dateString, filterQuery, filterFeatures,
                            filterAccess, filterTags, filterStatus, filterAttachments, filterMoods,
                            filterStartDateObj, filterEndDateObj, showInvalidEntries)) {
                            entities.push(entitySetup);
                        }
                    });
            }

            // Habits
            if (filterFeatures.length === 0 || filterFeatures.includes('habit')) {
                this.habitStore.setups()
                    .filter(s => !s.deletedAt && !hiddenHabitsMap[s.id])
                    .filter(s => this.entityAppearsOnDate(s, dateString, showInvalidEntries))
                    .forEach(entity => {
                        const entitySetup = this.getEntitySetup(entity, dateString);
                        if (this.passesAllFilters(entitySetup, dateString, filterQuery, filterFeatures,
                            filterAccess, filterTags, filterStatus, filterAttachments, filterMoods,
                            filterStartDateObj, filterEndDateObj, showInvalidEntries)) {
                            entities.push(entitySetup);
                        }
                    });
            }

            // Journals
            if (filterFeatures.length === 0 || filterFeatures.includes('journal')) {
                this.journalStore.setups()
                    .filter(s => !s.deletedAt && !hiddenJournalsMap[s.id])
                    .filter(s => this.entityAppearsOnDate(s, dateString, showInvalidEntries))
                    .forEach(entity => {
                        const entitySetup = this.getEntitySetup(entity, dateString);
                        if (this.passesAllFilters(entitySetup, dateString, filterQuery, filterFeatures,
                            filterAccess, filterTags, filterStatus, filterAttachments, filterMoods,
                            filterStartDateObj, filterEndDateObj, showInvalidEntries)) {
                            entities.push(entitySetup);
                        }
                    });
            }

            // Money Tracker Transactions
            if (filterFeatures.length === 0 || filterFeatures.includes('moneyTracker')) {
                this.moneyTrackerStore.transactions()
                    .filter(t => !t.deletedAt && !hiddenMoneytrackersMap[t.setupId])
                    .filter(t => this.entityAppearsOnDate(t, dateString, showInvalidEntries))
                    .forEach(entity => {
                        const entitySetup = this.getEntitySetup(entity, dateString);
                        if (this.passesAllFilters(entitySetup, dateString, filterQuery, filterFeatures,
                            filterAccess, filterTags, filterStatus, filterAttachments, filterMoods,
                            filterStartDateObj, filterEndDateObj, showInvalidEntries)) {
                            entities.push(entitySetup);
                        }
                    });
            }

            // Calendar Events
            if (filterFeatures.length === 0 || filterFeatures.includes('calendarIntegration')) {
                this.calendarStore.eventSetups()
                    .filter(e => !e.deletedAt &&
                        !hiddenCalendarsMap[e.calendarIntegrationId] &&
                        activeCalendarIntegrationIds.has(e.calendarIntegrationId) &&
                        !disabledCalendarGroupIds.has(e.calendarId))
                    .filter(e => this.entityAppearsOnDate(e, dateString, showInvalidEntries))
                    .forEach(entity => {
                        const entitySetup = this.getEntitySetup(entity, dateString);
                        if (this.passesAllFilters(entitySetup, dateString, filterQuery, filterFeatures,
                            filterAccess, filterTags, filterStatus, filterAttachments, filterMoods,
                            filterStartDateObj, filterEndDateObj, showInvalidEntries)) {
                            entities.push(entitySetup);
                        }
                    });
            }
        }

        // Process Focus entities
        if (showFocusFeature && shouldActuallyShowFocus) {
            const focusPushedIds = new Set<string>();

            this.focusStore.focus()
                .filter(f => !f.deletedAt)
                .forEach(focus => {
                    // Check if any attempt matches this date
                    const attempts = Object.values(focus.attempts);
                    const hasAttemptOnDate = attempts.some(attempt =>
                        getDateString(attempt.startAt) === dateString
                    );

                    if (hasAttemptOnDate && !focusPushedIds.has(focus.id)) {
                        focusPushedIds.add(focus.id);
                        const entitySetup = this.getEntitySetup(focus, dateString);
                        if (this.passesFocusFilters(entitySetup, dateString, filterAccess,
                            filterStartDateObj, filterEndDateObj)) {
                            entities.push(entitySetup);
                        }
                    }
                });
        }


        return entities;
    }

    /**
     * ✅ Get all past entities for hashtag grouping
     * Returns entities from the past (before today) with filters applied
     * Limited to past 2 years for performance
     */
    getAllPastEntities(
        filterQuery: string = '',
        filterFeatures: EntityName[] = [],
        filterAccess: string[] = [],
        filterTags: string[] = [],
        filterStatus: EntityStatus[] = [],
        filterAttachments: AttachmentType[] = [],
        filterMoods: number[] = [],
        filterStartDate: Date | null = null,
        filterEndDate: Date | null = null,
        showInvalidEntries: boolean = false
    ): CommonEntitySetup[] {
        const entities: CommonEntitySetup[] = [];
        const entityIdSet = new Set<string>(); // To avoid duplicates from repeating entities

        // Get visibility settings
        const showTodoFeature = this.viewSettingsStore.showTodoFeature();
        const showFocusFeature = this.viewSettingsStore.showFocusFeature();
        const hiddenHabitsMap = this.viewSettingsStore.hiddenHabits() || {};
        const hiddenJournalsMap = this.viewSettingsStore.hiddenJournals() || {};
        const hiddenMoneytrackersMap = this.viewSettingsStore.hiddenMoneytrackers() || {};
        const hiddenCalendarsMap = this.viewSettingsStore.hiddenCalendars() || {};
        const activeCalendarIntegrationIds = new Set(
            this.calendarStore.accounts().filter(a => !a.deletedAt).map(a => a.id)
        );
        const disabledCalendarGroupIds = new Set<string>();
        this.calendarStore.accounts().forEach(account => {
            if (account.deletedAt) return;
            account.calendarGroups?.forEach(group => {
                if (group.syncStatus === 'disabled') {
                    disabledCalendarGroupIds.add(group.id);
                }
            });
        });

        // Define date range for past entities (limited to 2 years for performance)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const twoYearsAgo = new Date(today);
        twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

        const toDateString = getDateString(yesterday);
        const fromDateString = getDateString(twoYearsAgo);

        // Pre-compute filter date objects
        const filterStartDateObj = filterStartDate ? new Date(filterStartDate) : null;
        const filterEndDateObj = filterEndDate ? new Date(filterEndDate) : null;
        if (filterStartDateObj) filterStartDateObj.setHours(0, 0, 0, 0);
        if (filterEndDateObj) filterEndDateObj.setHours(23, 59, 59, 999);

        // Helper to add entity if it passes filters
        const addEntityIfValid = (entity: any, dateString: string) => {
            const uniqueKey = `${entity.id}-${dateString}`;
            if (entityIdSet.has(uniqueKey)) return;

            const entitySetup = this.getEntitySetup(entity, dateString);
            if (this.passesAllFilters(entitySetup, dateString, filterQuery, filterFeatures,
                filterAccess, filterTags, filterStatus, filterAttachments, filterMoods,
                filterStartDateObj, filterEndDateObj, showInvalidEntries)) {
                entityIdSet.add(uniqueKey);
                entities.push(entitySetup);
            }
        };

        // Todos
        if (showTodoFeature && (filterFeatures.length === 0 || filterFeatures.includes('todo'))) {
            this.todoStore.todos()
                .filter(t => !t.deletedAt)
                .forEach(entity => {
                    const eligibleDates = this.checkDateInRange(
                        fromDateString, toDateString, 'todos',
                        entity.startAt, entity.endAt, entity.repeat || [], null, null
                    );
                    eligibleDates.forEach(dateString => addEntityIfValid(entity, dateString));
                });
        }

        // Habits
        if (filterFeatures.length === 0 || filterFeatures.includes('habit')) {
            const habitMap = this.habitStore.habitMap().setups;
            const idToSetup = this.habitStore.idToSetup();

            // Step 1: Generate eligible dates from repeat rules (valid entries)
            this.habitStore.setups()
                .filter(s => !s.deletedAt && !hiddenHabitsMap[s.id])
                .forEach(entity => {
                    const eligibleDates = this.checkDateInRange(
                        fromDateString, toDateString, 'habitSetups',
                        entity.startAt, entity.endAt, entity.repeat || [], null, null, false
                    );
                    eligibleDates.forEach(dateString => {
                        addEntityIfValid(entity, dateString);
                    });
                });

            // Step 2: Scan habitMap for invalid entries (instances that exist but shouldn't)
            if (showInvalidEntries) {
                Object.keys(habitMap).forEach(setupId => {
                    if (hiddenHabitsMap[setupId]) return;
                    const setup = idToSetup[setupId];
                    if (!setup || setup.deletedAt) return;
                    const dateMap = habitMap[setupId] || {};

                    Object.keys(dateMap).forEach(dateString => {
                        if (dateString < fromDateString || dateString > toDateString) return;
                        const uniqueKey = `${setupId}-${dateString}`;
                        // Only process if not already added (entityIdSet tracks this)
                        if (!entityIdSet.has(uniqueKey)) {
                            // This is an invalid entry (instance exists but shouldn't according to rules)
                            addEntityIfValid(setup, dateString);
                        }
                    });
                });
            }
        }

        // Journals
        if (filterFeatures.length === 0 || filterFeatures.includes('journal')) {
            const journalMap = this.journalStore.journalMap().setups;
            const idToSetup = this.journalStore.idToSetup();

            // Step 1: Generate eligible dates from repeat rules (valid entries)
            this.journalStore.setups()
                .filter(s => !s.deletedAt && !hiddenJournalsMap[s.id])
                .forEach(entity => {
                    const eligibleDates = this.checkDateInRange(
                        fromDateString, toDateString, 'journalSetups',
                        entity.startAt, entity.endAt, entity.repeat || [], null, null, false
                    );
                    eligibleDates.forEach(dateString => {
                        addEntityIfValid(entity, dateString);
                    });
                });

            // Step 2: Scan journalMap for invalid entries (instances that exist but shouldn't)
            if (showInvalidEntries) {
                Object.keys(journalMap).forEach(setupId => {
                    if (hiddenJournalsMap[setupId]) return;
                    const setup = idToSetup[setupId];
                    if (!setup || setup.deletedAt) return;
                    const dateMap = journalMap[setupId] || {};

                    Object.keys(dateMap).forEach(dateString => {
                        if (dateString < fromDateString || dateString > toDateString) return;
                        const uniqueKey = `${setupId}-${dateString}`;
                        // Only process if not already added (entityIdSet tracks this)
                        if (!entityIdSet.has(uniqueKey)) {
                            // This is an invalid entry (instance exists but shouldn't according to rules)
                            addEntityIfValid(setup, dateString);
                        }
                    });
                });
            }
        }

        // Money Tracker Transactions
        if (filterFeatures.length === 0 || filterFeatures.includes('moneyTracker')) {
            this.moneyTrackerStore.transactions()
                .filter(t => !t.deletedAt && !hiddenMoneytrackersMap[t.setupId])
                .forEach(entity => {
                    const eligibleDates = this.checkDateInRange(
                        fromDateString, toDateString, 'moneyTrackerActions',
                        null, null, [], entity.transactionDate, null
                    );
                    eligibleDates.forEach(dateString => addEntityIfValid(entity, dateString));
                });
        }

        // Calendar Events
        if (filterFeatures.length === 0 || filterFeatures.includes('calendarIntegration')) {
            const setupToEventsMap = this.calendarStore.setupToEventsMap();

            this.calendarStore.eventSetups()
                .filter(e => !e.deletedAt &&
                    !hiddenCalendarsMap[e.calendarIntegrationId] &&
                    activeCalendarIntegrationIds.has(e.calendarIntegrationId) &&
                    !disabledCalendarGroupIds.has(e.calendarId))
                .forEach(entity => {
                    const eligibleDates = this.checkDateInRange(
                        fromDateString, toDateString, 'calendarEventSetups',
                        entity.startAt, entity.endAt, entity.repeat || [], null, null
                    );

                    // Filter out RRULE dates that are replaced by exceptions
                    const eventsForSetup = setupToEventsMap[entity.id];
                    const filteredDates = eligibleDates.filter(dateString => {
                        if (eventsForSetup) {
                            const hasReplacingException = Object.values(eventsForSetup).some(
                                event => event.originalStartAt?.dateString === dateString
                            );
                            if (hasReplacingException) return false;
                        }
                        return true;
                    });

                    filteredDates.forEach(dateString => addEntityIfValid(entity, dateString));
                });

            // Add exception occurrences that are moved TO dates in range
            for (const [setupId, eventsByDate] of Object.entries(setupToEventsMap)) {
                for (const [dateString, event] of Object.entries(eventsByDate)) {
                    if (dateString < fromDateString || dateString > toDateString) continue;
                    if (!event.originalStartAt || event.deletedAt) continue;

                    const setup = this.calendarStore.idToEventSetup()[setupId];
                    if (!setup || setup.deletedAt) continue;
                    if (hiddenCalendarsMap[setup.calendarIntegrationId]) continue;
                    if (!activeCalendarIntegrationIds.has(setup.calendarIntegrationId)) continue;
                    if (disabledCalendarGroupIds.has(setup.calendarId)) continue;

                    addEntityIfValid(setup, dateString);
                }
            }
        }

        // Focus entities (if no non-focus filters applied)
        const hasNonFocusFilters = filterQuery.length > 0 ||
            filterTags.length > 0 ||
            filterAttachments.length > 0 ||
            filterMoods.length > 0 ||
            filterStatus.length > 0;

        if (showFocusFeature && (filterFeatures.length === 0 || filterFeatures.includes('focus')) && !hasNonFocusFilters) {
            this.focusStore.focus()
                .filter(f => !f.deletedAt)
                .forEach(focus => {
                    const attempts = Object.values(focus.attempts);
                    attempts.forEach(attempt => {
                        const dateString = getDateString(attempt.startAt);
                        if (dateString >= fromDateString && dateString <= toDateString) {
                            const uniqueKey = `${focus.id}-${dateString}`;
                            if (!entityIdSet.has(uniqueKey)) {
                                const entitySetup = this.getEntitySetup(focus, dateString);
                                if (this.passesFocusFilters(entitySetup, dateString, filterAccess,
                                    filterStartDateObj, filterEndDateObj)) {
                                    entityIdSet.add(uniqueKey);
                                    entities.push(entitySetup);
                                }
                            }
                        }
                    });
                });
        }

        return entities;
    }

    checkDateInRange(fromDateString: string, toDateString: string, collection: Collection, startAt: CustomDate | null, endAt: CustomDate | null, repeat: string[], dueAt: CustomDate | null, deletedAt: Date | null, showInvalidEntries: boolean = false): string[] {
        if (collection === 'moneyTrackerActions') {
            if (!dueAt) return [];
            if (dueAt.dateString < fromDateString || dueAt.dateString > toDateString) return [];
            return [dueAt.dateString];
        } else {
            if (!startAt) return [];
            if (repeat.length === 0) {
                if (startAt.dateString < fromDateString || startAt.dateString > toDateString) {
                    return [];
                } else {
                    return [startAt.dateString];
                };
            } else {
                const endDateStr = endAt?.dateString || '9999-12-31';
                if (startAt.dateString > toDateString || endDateStr < fromDateString) return [];
                const ruleRecord = this.ruleToRuleRecord(repeat[0] || '');
                const eligibleDates = this.getAllDatesOfEntity(ruleRecord, startAt.dateString, endDateStr, fromDateString, toDateString, showInvalidEntries);
                return eligibleDates;
            }
        }
    };

    getEntitySetup(entity: any, dateString: string): CommonEntitySetup {
        const entityCollection = entity.entityType;
        const entityName: EntityName = entityCollection === 'habitSetups' ? 'habit' : entityCollection === 'journalSetups' ? 'journal' : entityCollection === 'moneyTrackerActions' ? 'moneyTracker' : entityCollection === 'calendarEventSetups' ? 'calendarIntegration' : entityCollection === 'todos' ? 'todo' : entityCollection;
        const { status, percentage, hashtags, attachmentTypes, mood } = this.getMeta(entityName, entity, dateString);

        const isInvalid = status === 'missed';
        const role = entity.uid === this.cc.user.uid ? 'owner' : getRoleByUid(entity.members, this.cc.user.uid) || 'viewer';

        // Determine startAt for sorting - handle different entity types
        let startAtValue = entity.startAt || entity.dueAt || null;
        let invalidEntry = false;

        // ✅ Handle habits - check for actual instance and determine if invalid
        if (entityName === 'habit') {
            const habit = this.habitStore.habitMap().setups[entity.id]?.[dateString] || null;
            if (habit) {
                // Habit instance exists - use its dueAt as startAtValue
                startAtValue = habit.dueAt;
                // Check if it should appear on this date according to repeat rules
                const shouldAppear = this.entityAppearsOnDate(entity, dateString, false);
                invalidEntry = !shouldAppear;
            } else {
                // No instance - use the dateString parameter to create proper date value
                // This ensures each date (25, 26, 27) has the correct date in startAt
                startAtValue = { dateString, timeString: entity.startAt?.timeString || '', dueAt: new Date(dateString + 'T00:00:00'), dateTime: new Date(dateString + 'T00:00:00'), timeWithOffset: '' } as any;
            }
        }
        // ✅ Handle journals - check for actual instance and determine if invalid
        else if (entityName === 'journal') {
            const journal = this.journalStore.journalMap().setups[entity.id]?.[dateString] || null;
            if (journal) {
                // Journal instance exists - use its dueAt as startAtValue
                startAtValue = journal.dueAt;
                // Check if it should appear on this date according to repeat rules
                const shouldAppear = this.entityAppearsOnDate(entity, dateString, false);
                invalidEntry = !shouldAppear;
            } else {
                // No instance - use the dateString parameter to create proper date value
                startAtValue = { dateString, timeString: entity.startAt?.timeString || '', dueAt: new Date(dateString + 'T00:00:00'), dateTime: new Date(dateString + 'T00:00:00'), timeWithOffset: '' } as any;
            }
        }
        // ✅ Handle calendar events - check for exception action data
        else if (entityName === 'calendarIntegration') {
            const setupToEventsMap = this.calendarStore.setupToEventsMap();
            const eventOnDate = setupToEventsMap[entity.id]?.[dateString];
            if (eventOnDate) {
                // Use the action's startAt for correct time display
                startAtValue = eventOnDate.startAt;
            }
            if (entity.start) {
                const startDate = new Date(entity.start);
                const hours = startDate.getHours().toString().padStart(2, '0');
                const minutes = startDate.getMinutes().toString().padStart(2, '0');
                startAtValue = startAtValue || ({ timeString: `${hours}:${minutes}`, dateString: '', dateTime: startDate, timeWithOffset: '' } as any);
            }
        }

        return {
            id: entity.id,
            entityName: entityName,
            title: entityCollection === 'focus' ? 'Focus Trainer' : entity.title,
            invalid: isInvalid,
            status: status,
            tags: hashtags,
            percentage: percentage,
            role: role,
            attachmentTypes: attachmentTypes,
            mood: mood,
            startAt: startAtValue,
            invalidEntry: invalidEntry,
        }
    };

    getMeta(entityName: EntityName, entity: any, dateString: string): { status: EntityStatus, percentage: number, hashtags: string[], attachmentTypes: AttachmentType[], mood: number | null } {
        switch (entityName) {
            case 'focus':
                return { status: 'none', percentage: 0, hashtags: [], attachmentTypes: [], mood: null };
            case 'todo':
                const todoStatus = this.todoService.getStatus(entity, dateString);
                const todoHashtags = entity.tags || [];
                const todoValidHashtags = this.ss.getValidHashtags(todoHashtags);
                const todoAttachments = entity.attachments ? Object.values(entity.attachments) as Attachment[] : [];
                const todoAttachmentTypes = todoAttachments.filter((attachment: Attachment) => attachment.deletedAt === null).map((attachment: Attachment) => attachment.fileType);
                return { status: todoStatus, percentage: 0, hashtags: todoValidHashtags, attachmentTypes: todoAttachmentTypes, mood: null };

            case 'habit':
                const habit = this.habitStore.habitMap().setups[entity.id]?.[dateString] || null;
                const habitStatus = this.habitService.getStatus(habit, dateString);
                const habitSetupTags = entity.tags || [];
                const habitTags = habit ? habit.tags : [];
                const habitHashtags = [...habitSetupTags, ...habitTags];
                const habitValidHashtags = this.ss.getValidHashtags(habitHashtags);
                const habitAttachments = habit ? Object.values(habit.attachments) as Attachment[] : [];
                const habitAttachmentTypes = habitAttachments.filter((attachment: Attachment) => attachment.deletedAt === null).map((attachment: Attachment) => attachment.fileType);
                return { status: habitStatus.status, percentage: habitStatus.percentage, hashtags: habitValidHashtags, attachmentTypes: habitAttachmentTypes, mood: null };

            case 'journal':
                const journal = this.journalStore.journalMap().setups[entity.id]?.[dateString] || null;
                const journalStatus = this.journalService.getStatus(journal, dateString);
                const journalSetupTags = entity.tags || [];
                const journalTags = journal ? journal.tags : [];
                const journalHashtags = [...journalSetupTags, ...journalTags];
                const journalValidHashtags = this.ss.getValidHashtags(journalHashtags);
                const journalAttachments = journal ? Object.values(journal.attachments) as Attachment[] : [];
                const journalAttachmentTypes = journalAttachments.filter((attachment: Attachment) => attachment.deletedAt === null).map((attachment: Attachment) => attachment.fileType);
                return { status: journalStatus, percentage: 0, hashtags: journalValidHashtags, attachmentTypes: journalAttachmentTypes, mood: journal ? journal.emotion : null };

            case 'moneyTracker':
                const moneyTrackerTags = entity.tags || [];
                const moneyTrackerSetup = this.moneyTrackerStore.idToSetup()[entity.setupId];
                const moneyTrackerSetupTags = moneyTrackerSetup ? moneyTrackerSetup.tags : [];
                const moneyTrackerHashtags = [...moneyTrackerTags, ...moneyTrackerSetupTags];
                const moneyTrackerValidHashtags = this.ss.getValidHashtags(moneyTrackerHashtags);
                const moneyTrackerAttachments = entity.attachments ? Object.values(entity.attachments) as Attachment[] : [];
                const moneyTrackerAttachmentTypes = moneyTrackerAttachments.filter((attachment: Attachment) => attachment.deletedAt === null).map((attachment: Attachment) => attachment.fileType);
                return { status: 'all', percentage: 0, hashtags: moneyTrackerValidHashtags, attachmentTypes: moneyTrackerAttachmentTypes, mood: null };

            case 'calendarIntegration':
                const calendarStatus = this.calendarIntegrationService.getStatus(entity.id, dateString);
                return { status: calendarStatus, percentage: 0, hashtags: [], attachmentTypes: [], mood: null };
            default:
                return { status: 'all', percentage: 0, hashtags: [], attachmentTypes: [], mood: null };
        }
    };

    getAllDatesOfEntity(ruleRecord: Record<string, string>, startDate: string, endDate: string | null, fromDateString: string, toDateString: string, showInvalidEntries: boolean = false): string[] {
        if (!startDate) return [];
        log.debug('getAllDatesOfEntity', 'Function called', { ruleRecord, startDate, endDate, fromDateString, toDateString });
        const isOccurrenceBasedRule = ruleRecord['COUNT'] ? true : false;
        if (isOccurrenceBasedRule) {
            const dateStrings: string[] = [];
            const count = Number(ruleRecord['COUNT'] || 0);
            if (count <= 0) return [];

            const currentDate = new Date(startDate);

            while (dateStrings.length < count) {
                log.debug('getAllDatesOfEntity', 'Processing date', { isEligible: this.isElligible(currentDate, ruleRecord), count, currentLength: dateStrings.length });
                if (this.isElligible(currentDate, ruleRecord)) {
                    const dateString = currentDate.toISOString().slice(0, 10);
                    dateStrings.push(dateString);
                }
                currentDate.setDate(currentDate.getDate() + 1); // increment by 1 day
            }
            return dateStrings.filter(date => date >= fromDateString && date <= toDateString);
        } else {
            const dateStrings: string[] = [];
            const allDates = this.getDateRange(fromDateString, toDateString);
            allDates.forEach(date => {
                if ((this.isElligible(new Date(date), ruleRecord) || showInvalidEntries) && date >= startDate && (endDate ? date <= endDate : true)) {
                    dateStrings.push(date);
                }
            });
            return dateStrings;
        }
    }

    isElligible(date: Date, ruleRecord: Record<string, string>): boolean {
        const parsedDateInfo = this.parseDateInfo(date);

        switch (ruleRecord['FREQ']) {
            case 'DAILY':
                return true;

            case 'WEEKLY':
                const days = ruleRecord['BYDAY'].split(',') as DayCode[];
                return days.includes(parsedDateInfo[1]);

            case 'MONTHLY':
                if (ruleRecord['BYMONTHDAY'] && ruleRecord['BYMONTH']) {
                    const days = ruleRecord['BYMONTHDAY'].split(',').map(Number);
                    const months = ruleRecord['BYMONTH'].split(',').map(Number);
                    return days.includes(Number(parsedDateInfo[0])) && months.includes(Number(parsedDateInfo[3]));
                } else if (ruleRecord['BYDAY']) {
                    const byDayValues = ruleRecord['BYDAY'].split(',');
                    return byDayValues.some(item => {
                        const match = item.match(/^(-?\d)?([A-Z]{2})$/);
                        if (match) {
                            const [, weekNum, dayCode] = match;
                            if (weekNum) {
                                const ordinal = weekNum;
                                return ordinal === parsedDateInfo[2] && dayCode === parsedDateInfo[1];
                            }
                        }
                        return false;
                    });
                } else {
                    return false;
                }

            case 'YEARLY':
                const day = ruleRecord['BYMONTHDAY'];
                const month = ruleRecord['BYMONTH'];
                return month === parsedDateInfo[3] && day === parsedDateInfo[0];
            default:
                return false;
        }
    };

    ruleToRuleRecord(rrule: string): Record<string, string> {
        if (!rrule) return {};
        const ruleParts = rrule.replace('RRULE:', '').split(';');
        const ruleRecord: Record<string, string> = {};

        ruleParts.forEach(part => {
            const [key, value] = part.split('=');
            ruleRecord[key] = value;
        });
        return ruleRecord;
    };

    parseDateInfo(date: Date): [string, DayCode, string, string, boolean] {

        const day = String(date.getDate());
        const weekday = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'][date.getDay()];

        const dayOfMonth = date.getDate();
        const nthWeek = Math.floor((dayOfMonth - 1) / 7) + 1;

        const isLastDay = dayOfMonth === new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

        const month = String(date.getMonth() + 1);

        return [day, weekday as DayCode, String(nthWeek), month, isLastDay];
    };

    getDateRange(startDateStr: string, endDateStr: string): string[] {
        // Parse start and end dates
        const start = new Date(startDateStr);
        const end = new Date(endDateStr);

        // Normalize both dates to midnight
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);

        const result: string[] = [];

        // Determine the loop direction (forward or backward)
        const step = start <= end ? 1 : -1;

        for (let d = new Date(start); (step > 0 ? d <= end : d >= end); d.setDate(d.getDate() + step)) {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            result.push(`${year}-${month}-${day}`);
        }

        // If traversing backward, reverse so earliest date is first
        return step === -1 ? result.reverse() : result;
    };

    getRemainingDays(repeat: string[], startDateStr: string, endDateStr: string | null, currentDateStr: string): number | null {
        const ruleRecord = this.ruleToRuleRecord(repeat[0] || '');
        const isOccurrenceBasedRule = ruleRecord['COUNT'] ? true : false;
        if (!isOccurrenceBasedRule && !endDateStr) return null;
        const eligibleDates = this.getAllDatesOfEntity(ruleRecord, startDateStr, endDateStr, startDateStr, endDateStr ?? '9999-12-31');
        if (!eligibleDates || eligibleDates.length === 0) return null;

        // Filter to only dates >= current date (remaining occurrences)
        const remainingDates = eligibleDates.filter(d => d >= currentDateStr);

        // If no remaining dates, the repeat has already ended
        if (remainingDates.length === 0) return null;

        // Only show badge when there are 1-4 remaining occurrences
        // Return count: 1 = last occurrence (today), 2 = 2 occurrences left, 3 = 3 left, 4 = 4 left
        // Note: UI displays as "days" per QA requirement, but value represents occurrences
        if (remainingDates.length <= 4) {
            return remainingDates.length;
        }

        return null;
    }



    /**
 * Calculates the remaining days until a journal/habit ends
 * Returns null if banner should not be shown, otherwise returns the number of days remaining
 *
 * @param journalSetup - The journal setup object containing repeat, startAt, and endAt
 * @param showBanner - Whether the banner should be shown
 * @returns number of days remaining or null
 */
    getRemainingDayss(
        repeat: string[],
        tmzSafeStartAt: Date,
        tmzSafeEndAt: Date | null
    ): number | null {
        // Get the last date of repeat or use the endAt date
        const lastDateOfRepeat = this.getLastDateOfRepeat(
            repeat,
            tmzSafeStartAt,
            tmzSafeEndAt
        ) ?? tmzSafeEndAt;

        // Calculate difference in days
        return lastDateOfRepeat != null
            ? this.getDiffInDaysInt(new Date(), lastDateOfRepeat)
            : null;
    }

    /**
     * Gets the last possible date of a repeating entity
     * For non-repeating entities, returns the endAt date
     * For repeating entities, calculates the last occurrence based on the repeat rule
     *
     * @param repeatData - Array of RRULE strings
     * @param startDate - Start date of the entity
     * @param endDate - End date of the entity (can be null for never-ending entities)
     * @returns Last date of the repeat or null
     */
    getLastDateOfRepeat(
        repeatData: string[],
        startDate: Date,
        endDate: Date | null
    ): Date | null {
        // If no repeat rule, return the endDate
        if (!repeatData || repeatData.length === 0) {
            return endDate;
        }

        // If endDate is null, entity never ends
        if (endDate === null) {
            return null;
        }

        // For repeating entities with an end date, return the end date
        // (In the Flutter code, this uses RRule parsing to get exact last occurrence)
        // For simplicity in Angular, we can return the endDate as the last possible date
        return endDate;
    }

    /**
     * Calculates the difference in days between two dates
     * Adds 1 to include both start and end dates in the count
     *
     * @param startDate - Start date
     * @param endDate - End date
     * @returns Number of days difference (inclusive)
     */
    getDiffInDaysInt(startDate: Date, endDate: Date): number {
        // Convert to local time and strip time component
        const start = this.onlyDate(new Date(startDate));
        const end = this.onlyDate(new Date(endDate));

        // Calculate difference in days
        const diffInMs = end.getTime() - start.getTime();
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        // Add 1 to make it inclusive (same as Flutter implementation)
        return diffInDays + 1;
    }

    /**
     * Returns a date with only the date part (time set to 00:00:00)
     *
     * @param date - Input date
     * @returns Date with time set to midnight
     */
    onlyDate(date: Date): Date {
        const result = new Date(date);
        result.setHours(0, 0, 0, 0);
        return result;
    }
}
